/**
 * The operator console's UI kit. Screens import everything from here:
 *
 *   primitives  Button, IconButton, Badge, Count, StatusDot, Kbd, Progress, Spinner, Tooltip, motion hooks
 *   form        Field, Input, Select, Textarea, Switch, Toggle, Checkbox, Chip
 *   surfaces    Panel, Card, Toolbar, Spacer, Tabs, SegmentedControl, Callout
 *   data        StatCard, CountUp, Sparkline, Skeleton, SkeletonText, Empty, EmptyState, IdChip, Avatar, KeyValue
 *   table       Table, tableClass, SkeletonRows, TableSkeleton, Pager
 *   overlay     Modal, Drawer, ToastProvider, useToast
 *   format      when, short, money, toNumber, decimalsOf, formatNumber
 *
 * This file adds the `ui` layout helpers, API error texts, the TOTP prompt
 * and the status pill. Tokens live in styles/admin.css.
 */
import type { ReactNode } from 'react'
import { ShieldCheck } from 'lucide-react'
import { createContext, useCallback, useContext, useMemo, useState } from 'react'

import { ApiError } from '../../lib/http'
import { Field, Input } from './form'
import { Modal } from './overlay'
import { Badge, Button, type Tone } from './primitives'
import styles from './Ui.module.css'

export {
  Badge,
  Button,
  Count,
  IconButton,
  Kbd,
  Progress,
  Spinner,
  StatusDot,
  Tooltip,
  cx,
  prefersReducedMotion,
  toneClass,
  useCountUp,
  usePrefersReducedMotion,
  type BadgeProps,
  type ButtonProps,
  type ButtonSize,
  type ButtonVariant,
  type IconButtonProps,
  type Tone,
  type TooltipSide,
} from './primitives'
export {
  Checkbox,
  Chip,
  Field,
  Input,
  Select,
  Switch,
  Textarea,
  Toggle,
  type CheckboxProps,
  type ChipProps,
  type FieldProps,
  type InputProps,
  type SelectProps,
  type SwitchProps,
  type TextareaProps,
  type ToggleProps,
} from './form'
export {
  Callout,
  Card,
  Panel,
  SegmentedControl,
  Spacer,
  Tabs,
  Toolbar,
  type CalloutProps,
  type CardProps,
  type SegmentedControlProps,
  type TabOption,
  type TabsProps,
} from './surfaces'
export {
  Avatar,
  CountUp,
  Empty,
  EmptyState,
  IdChip,
  KeyValue,
  Skeleton,
  SkeletonText,
  Sparkline,
  StatCard,
  copyText,
  initials,
  type CountUpProps,
  type EmptyStateProps,
  type IdChipProps,
  type KeyValueItem,
  type SkeletonProps,
  type SparklineProps,
  type StatCardProps,
} from './data'
export { Pager, SkeletonRows, Table, TableSkeleton, tableClass, type TableProps } from './table'
export { Drawer, Modal, ToastProvider, useToast, type DrawerProps, type ModalProps } from './overlay'
export { decimalsOf, formatNumber, money, short, toNumber, when } from './format'

/* ------------------------------------------------------------------------ */
/* layout and text helpers                                                  */
/* ------------------------------------------------------------------------ */

/**
 * Class names for plain markup: grid2/grid3 (form grids), stack (16 px
 * column), sectionTitle (caption with a hairline), chips (chip row), kv and
 * facts (older <dl> lists; prefer <KeyValue>), json (a code block), code/mono
 * (figures), hint (quiet 12 px line), error (red line), link (in-text link).
 */
export const ui = {
  grid2: styles.grid2,
  grid3: styles.grid3,
  stack: styles.stack,
  sectionTitle: styles.sectionTitle,
  chips: styles.chips,
  kv: styles.kv,
  json: styles.json,
  code: styles.code,
  mono: styles.mono,
  hint: styles.hint,
  facts: styles.facts,
  error: styles.error,
  link: styles.link,
}

/* ------------------------------------------------------------------------ */
/* API errors                                                               */
/* ------------------------------------------------------------------------ */

/** Human words for an API failure, or the raw code when there are none. */
export function describeError(error: unknown): string {
  if (error instanceof ApiError) {
    switch (error.code) {
      case 'TOTP_NOT_ENROLLED':
        return 'Сначала включите двухфакторную защиту в разделе «Безопасность».'
      case 'TOTP_REQUIRED':
        return 'Нужен код из приложения-аутентификатора.'
      case 'TOTP_INVALID':
        return 'Код не подошёл. Проверьте время на телефоне и введите новый.'
      case 'TOTP_REPLAYED':
        return 'Этот код уже использован. Дождитесь следующего.'
      case 'WITHDRAWAL_BROADCAST_UNAVAILABLE':
        return 'Для этой сети нет горячего кошелька: отправьте вручную и запишите хеш транзакции.'
      case 'WITHDRAWAL_INVALID_TRANSITION':
        return 'Заявка уже в другом статусе. Обновите список.'
      case 'WITHDRAWAL_BROADCAST_FAILED':
        return 'Сеть не приняла транзакцию. Средства пользователя остаются в резерве, причина — в строке заявки.'
      case 'WITHDRAWAL_BROADCAST_UNCERTAIN':
        return 'Транзакция могла уйти в сеть. Сверьте хеш в обозревателе, затем запишите его или отметьте неудачу.'
      case 'WITHDRAWAL_BROADCAST_IN_PROGRESS':
        return 'Горячий кошелёк ещё отправляет эту заявку. Дождитесь результата.'
      case 'WITHDRAWAL_SEND_BUSY':
        return 'Горячий кошелёк этой сети занят другой отправкой. Заявка осталась в «К отправке» — повторите через минуту.'
      case 'WITHDRAWAL_SEND_LOCK_UNAVAILABLE':
        return 'Не удалось взять лок отправки: Redis не ответил. Ничего не подписано, заявка осталась в «К отправке» — повторите позже.'
      case 'WITHDRAWAL_BROADCAST_STILL_VALID':
        return 'Подписанная транзакция ещё может попасть в блок. Отметить неудачу можно после срока, указанного в строке заявки, если её так и нет в обозревателе.'
      case 'ADMIN_SELF_EDIT':
        return 'Свой аккаунт менять нельзя.'
      case 'REVERSAL_NOT_FOUND':
        return 'Такого сторнирования нет. Обновите список.'
      case 'REVERSAL_NOT_PENDING':
        return 'Решение по этому депозиту уже принято другим оператором. Обновите список.'
      case 'DEPOSIT_NOT_CREDITED':
        return 'Депозит уже не в статусе «зачислен» — сторнировать нечего.'
      case 'REVERSAL_IDEMPOTENCY_KEY_REUSED':
      case 'REVERSAL_IN_PROGRESS':
        return 'Предыдущая попытка ещё не завершилась. Закройте окно и откройте депозит заново.'
      case 'AUTH_FORBIDDEN_ROLE':
      case 'ADMIN_ONLY':
        return 'Этот раздел или действие не входит в права вашего стола. Его выполняет администратор или оператор с нужной ролью.'
      case 'ADMIN_ROLE_CHANGE_FORBIDDEN':
        return 'Роль оператора меняет только администратор.'
      case 'USER_IS_MASTER':
        return 'Аккаунт мастера управляется в разделе «Мастера и стратегии».'
      case 'WITHDRAWAL_SECOND_APPROVER_REQUIRED':
        return 'Вы уже одобрили эту заявку: второе одобрение даёт другой оператор.'
      case 'WITHDRAWAL_TX_HASH_INVALID':
        return 'Это не хеш транзакции сети заявки. Скопируйте хеш из обозревателя этой сети (не адрес и не ссылку).'
      case 'WITHDRAWAL_NOT_FOUND':
        return 'Такой заявки нет. Обновите список.'
      case 'INSUFFICIENT_BALANCE':
        return 'На субсчёте нет столько доступных средств: списание не может увести баланс в минус.'
      case 'ADJUSTMENT_AMOUNT_INVALID':
        return 'Сумма корректировки должна быть ненулевым числом.'
      case 'ADJUSTMENT_REASON_REQUIRED':
        return 'Укажите причину корректировки — не короче трёх символов.'
      case 'ADJUSTMENT_IDEMPOTENCY_KEY_REUSED':
      case 'ADJUSTMENT_IN_PROGRESS':
        return 'Предыдущая отправка этой корректировки ещё не завершилась или была другой. Обновите карточку и проверьте леджер, прежде чем повторять.'
      case 'ASSET_NOT_FOUND':
        return 'Такого актива нет в реестре. Проверьте тикер, например USDT.'
      case 'USER_NOT_FOUND':
        return 'Пользователь не найден. Обновите список.'
      case 'ORDER_PRICE_STALE':
      case 'ORDER_PRICE_UNAVAILABLE':
        return 'Цена фида устарела или недоступна. Укажите цену ликвидации в поле «Цена» — она сохранится в аудите как цена оператора.'
      case 'POSITION_NOT_OPEN':
        return 'Позиция уже закрыта или ликвидирована. Обновите список.'
      case 'POSITION_NOT_FOUND':
        return 'Такой позиции нет. Обновите список.'
      case 'KYC_APPLICATION_LOCKED':
        return 'Одобренную заявку изменить нельзя.'
      case 'KYC_STATUS_UNCHANGED':
        return 'Заявка уже в этом статусе. Обновите список.'
      case 'KYC_APPLICATION_NOT_FOUND':
        return 'Такой заявки нет. Обновите список.'
      case 'DOWNLOAD_FAILED':
        return error.status === 404 ? 'Файл не найден.' : error.status === 403 ? 'Нет прав открыть этот файл.' : 'Файл не загрузился. Повторите.'
      case 'VALIDATION_ERROR':
        return 'Сервер не принял данные формы. Проверьте поля и повторите.'
      case 'CHECK_VIOLATION':
        return 'Значение вне допустимого диапазона — база данных его не приняла. Проверьте поля и повторите.'
      case 'ORDER_FUTURES_DISABLED':
        return 'Фьючерсы выключены настройкой futures.enabled — её включает администратор в «Настройках». Для сигнала можно выбрать спотовый рынок.'
      case 'ORDER_MARKET_DISABLED':
        return 'Этот рынок выключен («Сети и активы» → «Рынки и комиссии»). Выберите другой рынок или включите этот.'
      case 'ORDER_MARKET_NOT_FOUND':
        return 'Такого рынка нет. Обновите страницу и выберите рынок заново.'
      case 'FEATURE_DISABLED':
        return 'Раздел выключен настройкой (для копи-трейдинга — copy.enabled). Включает администратор в «Настройках».'
      case 'INVITE_CODE_TAKEN':
        return 'Такой код уже занят — другим кодом приглашения или личным кодом участника. Введите другой или оставьте поле пустым: код придумает система.'
      case 'INVITE_CODE_NOT_FOUND':
        return 'Такого кода приглашения нет. Обновите список.'
      case 'INVITE_CODE_ALLOCATION_FAILED':
        return 'Не удалось подобрать свободный код. Нажмите «Создать» ещё раз.'
      case 'SIGNAL_CODE_EXISTS':
        return 'Сигнал с таким кодом уже есть. Введите другой код.'
      case 'SIGNAL_INVALID_WINDOW':
        return 'Окно кода пустое: окончание должно быть позже выпуска.'
      case 'SIGNAL_WINDOW_TOO_LONG': {
        // One signal's window may run up to a day (`details.maxMinutes`, 1440).
        const details = typeof error.details === 'object' && error.details !== null ? (error.details as { maxMinutes?: unknown }) : {}
        const limit = typeof details.maxMinutes === 'number' ? `Код может действовать не дольше ${details.maxMinutes} мин (24 ч)` : 'Окно кода длиннее суток'
        return `${limit}. Уменьшите длительность сигнала и сохраните.`
      }
      case 'SIGNAL_INVALID_AMOUNT_RANGE':
        return 'Суммы не подходят: доля — больше 0 и не больше 100 %, минимум и максимум — больше нуля, максимум не меньше минимума.'
      case 'SIGNAL_LEVERAGE_INVALID':
        return 'Плечо вне допустимого: от 1 до максимума рынка, на споте — только 1.'
      case 'SIGNAL_EXIT_INVALID':
        return 'Выходы не подходят: тейк-профит — больше 0 и меньше 1000 % (у шорта и продажи — меньше 100 %), стоп-лосс — больше 0 и меньше 100 %.'
      case 'SIGNAL_NOT_DRAFT':
        return 'Сигнал уже опубликован: менять можно только черновик. Обновите список.'
      case 'SIGNAL_NOT_PUBLISHABLE':
      case 'SIGNAL_NOT_CANCELLABLE':
        return 'Сигнал уже в другом статусе. Обновите список.'
      case 'SIGNAL_EXPIRED':
        return 'Окно кода уже закончилось. У черновика нажмите «Сейчас» рядом с выпуском и сохраните; истёкший сигнал не продлить — создайте новый.'
      case 'SIGNAL_NOT_FOUND':
        return 'Такого сигнала нет. Обновите список.'
      case 'TELEGRAM_CHANNEL_NOT_CONFIGURED':
        return 'Канал не настроен: задайте telegram.signals_channel в «Настройках» (@имя или id -100…). Если канал задан — на сервере нет TELEGRAM_BOT_TOKEN.'
      case 'TELEGRAM_CHANNEL_UNREACHABLE':
        return `Бот не может писать в канал: ${telegramChannelReason(error.details)}`
      default:
        return `${error.message} (${error.code})`
    }
  }
  return error instanceof Error ? error.message : 'Что-то пошло не так'
}

/**
 * Why the bot cannot post to the codes channel, in operator words. The API
 * sends `details.reason` (`not_admin` | `not_found` | `bad_token` | `network`
 * | `other`) and Telegram's own `details.description`. `bad_token` is
 * Telegram's 401 (or 404 for a malformed token): the bot token itself is
 * refused; a bot removed from the channel comes back as `not_admin`.
 * `forbidden` is the older server's name for `bad_token`.
 */
export function telegramChannelReason(details: unknown): string {
  const record = typeof details === 'object' && details !== null ? (details as { reason?: unknown; description?: unknown }) : {}
  const reason = typeof record.reason === 'string' ? record.reason : ''
  const description = typeof record.description === 'string' ? record.description.trim() : ''
  const said = description === '' ? '' : ` Telegram: «${description}».`
  switch (reason) {
    case 'not_admin':
      return `бот не администратор канала. Добавьте бота в администраторы канала с правом публиковать сообщения.${said}`
    case 'not_found':
      return `канал не найден. Проверьте @имя или числовой id -100… в настройке telegram.signals_channel; у закрытого канала — только id.${said}`
    case 'bad_token':
    case 'forbidden':
      return `Telegram не принял токен бота: TELEGRAM_BOT_TOKEN на сервере неверный или отозван. Выпустите новый токен в @BotFather, пропишите его в окружении API и перезапустите сервер.${said}`
    case 'network':
      return `Telegram не ответил. Повторите через минуту.${said}`
    default:
      return description !== '' ? `Telegram: «${description}».` : 'Telegram не объяснил причину. Проверьте настройку telegram.signals_channel и права бота.'
  }
}

/* ------------------------------------------------------------------------ */
/* second factor                                                            */
/* ------------------------------------------------------------------------ */

const TotpContext = createContext<() => Promise<string>>(() => Promise.reject(new Error('no TOTP provider')))

/**
 * Asks the operator for a one-time code right before an action that needs it.
 * `withTotp(async (code) => …)` opens the prompt, runs the action with the
 * code and closes; a cancelled prompt rejects with `null`.
 */
export function TotpProvider({ children }: { children: ReactNode }): JSX.Element {
  const [pending, setPending] = useState<{ resolve: (code: string) => void; reject: (reason: unknown) => void } | null>(null)
  const [code, setCode] = useState('')
  const request = useCallback(
    () =>
      new Promise<string>((resolve, reject) => {
        setCode('')
        setPending({ resolve, reject })
      }),
    [],
  )
  const close = useCallback(() => {
    pending?.reject(null)
    setPending(null)
  }, [pending])
  const submit = useCallback(() => {
    if (code.trim().length < 6) return
    pending?.resolve(code.trim())
    setPending(null)
  }, [code, pending])
  const value = useMemo(() => request, [request])
  return (
    <TotpContext.Provider value={value}>
      {children}
      <Modal
        open={pending !== null}
        onClose={close}
        title="Код подтверждения"
        subtitle="Шесть цифр из приложения-аутентификатора. Каждый код действует на одно действие."
        size="sm"
        icon={<ShieldCheck />}
        footer={
          <>
            <Button variant="quiet" onClick={close}>
              Отмена
            </Button>
            <Button variant="primary" onClick={submit} disabled={code.trim().length < 6}>
              Подтвердить
            </Button>
          </>
        }
      >
        <Field label="Код">
          <Input
            mono
            autoFocus
            inputMode="numeric"
            className={styles.otp}
            maxLength={6}
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
            onKeyDown={(event) => {
              if (event.key === 'Enter') submit()
            }}
          />
        </Field>
      </Modal>
    </TotpContext.Provider>
  )
}

export function useTotp(): <T>(action: (code: string) => Promise<T>) => Promise<T | null> {
  const request = useContext(TotpContext)
  return useCallback(
    async <T,>(action: (code: string) => Promise<T>): Promise<T | null> => {
      let code: string
      try {
        code = await request()
      } catch {
        return null
      }
      // Callers run mutations whose onError already reports; a rejected
      // promise here would only surface as an uncaught error in the console.
      return action(code).catch(() => null)
    },
    [request],
  )
}

/* ------------------------------------------------------------------------ */
/* status                                                                   */
/* ------------------------------------------------------------------------ */

/** The Russian word for an API status (`pending` → «ожидает»); the raw value when unknown. */
export function statusLabel(status: string): string {
  return STATUS_RU[status] ?? status
}

const STATUS_ACCENT = new Set(['approved', 'sent', 'confirmed', 'active', 'running', 'live', 'paid', 'qualified', 'credited'])
const STATUS_WARNING = new Set(['pending', 'in_review', 'broadcasting', 'paused', 'resubmit', 'suspended', 'scheduled', 'pending_review'])
const STATUS_DOWN = new Set(['rejected', 'failed', 'banned', 'blocked', 'error', 'cancelled', 'reversed', 'revoked', 'liquidated'])
const STATUS_INFO = new Set(['detected', 'confirming', 'open'])
/** States that are happening right now: their dot breathes. */
const STATUS_LIVE = new Set(['broadcasting', 'running', 'live', 'confirming'])

/** The tone a status reads in: accent (done, good), warning (waits), down (failed, blocked), info (in flight), neutral. */
export function statusTone(status: string): Tone {
  if (STATUS_ACCENT.has(status)) return 'accent'
  if (STATUS_WARNING.has(status)) return 'warning'
  if (STATUS_DOWN.has(status)) return 'down'
  if (STATUS_INFO.has(status)) return 'info'
  return 'neutral'
}

export interface StatusPillProps {
  /** An API status (`pending`, `sent`, `live`…); shown in Russian. */
  status: string
  /** Override the text (the tone still follows `status`). */
  children?: ReactNode
  /** Override the tone. */
  tone?: Tone
  /** Force the breathing dot on or off (default: on for live states). */
  pulse?: boolean
  size?: 'sm' | 'md'
  title?: string
  className?: string
}

/** A status as a pill with a dot, in Russian and in its tone; live states (sending, running, on air) breathe. */
export function StatusPill({ status, children, tone, pulse, size, title, className }: StatusPillProps): JSX.Element {
  return (
    <Badge tone={tone ?? statusTone(status)} pulse={pulse ?? STATUS_LIVE.has(status)} size={size} title={title} className={className}>
      {children ?? STATUS_RU[status] ?? status}
    </Badge>
  )
}

const STATUS_RU: Record<string, string> = {
  pending: 'ожидает',
  approved: 'одобрен',
  rejected: 'отклонён',
  broadcasting: 'отправляется',
  sent: 'отправлен',
  confirmed: 'подтверждён',
  failed: 'не удался',
  in_review: 'на проверке',
  resubmit: 'нужна повторная подача',
  active: 'активен',
  suspended: 'приостановлен',
  banned: 'заблокирован',
  running: 'работает',
  stopped: 'остановлена',
  paused: 'на паузе',
  error: 'ошибка',
  blocked: 'заблокирован',
  user: 'пользователь',
  admin: 'админ',
  support: 'поддержка',
  finance: 'финансы',
  content: 'контент',
  master: 'мастер',
  draft: 'черновик',
  scheduled: 'запланирован',
  live: 'в эфире',
  expired: 'истёк',
  cancelled: 'отменён',
  pending_review: 'на проверке',
  paid: 'выплачено',
  reversed: 'отозвано',
  qualified: 'квалифицирован',
  revoked: 'отозван',
  applied: 'сторнировано',
  dismissed: 'снято',
  credited: 'зачислен',
  detected: 'обнаружен',
  confirming: 'подтверждается',
  below_minimum: 'ниже минимума',
  orphaned: 'выпал из сети',
  open: 'открыта',
  closed: 'закрыта',
  liquidated: 'ликвидирована',
}
